import api, { ApiResponse } from './index'
import { User } from '@/types/auth'

export const userApi = {
    // Get current user's profile
    getCurrentProfile: (): Promise<ApiResponse<{ user: User }>> =>
        api.get('/users/me'),

    // Update current user's profile
    updateProfile: (profileData: {
        firstName?: string;
        lastName?: string;
        phone?: string;
        bio?: string;
        location?: any;
        skills?: string[];
        hourlyRate?: number;
        availability?: string;
        languages?: string[];
        experience?: string;
        company?: string;
        website?: string;
    }): Promise<ApiResponse<{ user: User }>> =>
        api.put('/users/me', profileData),

    // Update notification preferences
    updateNotificationPreferences: (preferences: {
        emailNotifications?: boolean;
        pushNotifications?: boolean;
        smsNotifications?: boolean;
        gigAlerts?: boolean;
        applicationUpdates?: boolean;
        messageNotifications?: boolean;
        marketingEmails?: boolean;
    }): Promise<ApiResponse<{
        communicationPreferences: any;
        preferences: any;
    }>> =>
        api.put('/users/notifications', preferences),

    // Update privacy settings
    updatePrivacySettings: (settings: {
        profileVisibility?: 'public' | 'private' | 'registered';
        showLocation?: boolean;
        showContactInfo?: boolean;
        showLastSeen?: boolean;
    }): Promise<ApiResponse<{ privacy: any }>> =>
        api.put('/users/privacy', settings),

    // Update account security settings
    updateAccountSettings: (settings: {
        twoFactorEnabled?: boolean;
        loginAlerts?: boolean;
        sessionTimeout?: number;
    }): Promise<ApiResponse<{
        twoFactorEnabled: boolean;
        loginAlerts: boolean;
        sessionTimeout: number;
    }>> =>
        api.put('/users/account-settings', settings),

    // Update password
    updatePassword: (passwordData: {
        currentPassword: string;
        newPassword: string;
    }): Promise<ApiResponse> =>
        api.put('/users/password', passwordData),

    // Get user profile by ID
    getUserProfile: (userId: string): Promise<ApiResponse<{ user: User }>> =>
        api.get(`/users/${userId}`),

    // Get user profiles with filtering and pagination
    getUsers: (params: {
        page?: number
        limit?: number
        role?: 'worker' | 'employer'
        city?: string
        skills?: string[]
        verified?: boolean
        sortBy?: 'newest' | 'rating' | 'name'
    } = {}): Promise<ApiResponse<{
        users: User[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/users', { params }),

    // Search users
    searchUsers: (params: {
        query: string
        role?: 'worker' | 'employer'
        city?: string
        skills?: string[]
        page?: number
        limit?: number
    }): Promise<ApiResponse<{
        users: User[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/users/search', { params }),

    // Follow/unfollow user
    followUser: (userId: string): Promise<ApiResponse<{ isFollowing: boolean }>> =>
        api.post(`/users/${userId}/follow`),

    unfollowUser: (userId: string): Promise<ApiResponse<{ isFollowing: boolean }>> =>
        api.delete(`/users/${userId}/follow`),

    // Get user's followers
    getUserFollowers: (userId: string, params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        followers: User[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/users/${userId}/followers`, { params }),

    // Get users that the user is following
    getUserFollowing: (userId: string, params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        following: User[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/users/${userId}/following`, { params }),

    // Get user's ratings and reviews
    getUserRatings: (userId: string, params: {
        page?: number
        limit?: number
        sortBy?: 'newest' | 'oldest' | 'rating'
    } = {}): Promise<ApiResponse<{
        ratings: Array<{
            _id: string
            ratedBy: User
            gig: any
            rating: number
            comment: string
            createdAt: string
        }>
        averageRating: number
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/users/${userId}/ratings`, { params }),

    // Rate a user
    rateUser: (userId: string, data: {
        rating: number
        comment: string
        gigId: string
    }): Promise<ApiResponse<{
        rating: {
            _id: string
            rating: number
            comment: string
            createdAt: string
        }
    }>> =>
        api.post(`/users/${userId}/rate`, data),

    // Report a user
    reportUser: (userId: string, data: {
        reason: string
        description: string
        category: 'inappropriate_behavior' | 'spam' | 'fraud' | 'harassment' | 'other'
    }): Promise<ApiResponse> =>
        api.post(`/users/${userId}/report`, data),

    // Block a user
    blockUser: (userId: string): Promise<ApiResponse> =>
        api.post(`/users/${userId}/block`),

    // Unblock a user
    unblockUser: (userId: string): Promise<ApiResponse> =>
        api.delete(`/users/${userId}/block`),

    // Get blocked users
    getBlockedUsers: (params: {
        page?: number
        limit?: number
    } = {}): Promise<ApiResponse<{
        blockedUsers: User[]
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get('/users/blocked', { params }),

    // Get user's work history
    getUserWorkHistory: (userId: string, params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        applications: any[]
        totalCount: number
        currentPage: number
        totalPages: number
        stats: {
            completed: number
            averageRating: number
            totalEarnings: number
        }
    }>> =>
        api.get(`/users/${userId}/work-history`, { params }),

    // Get user's hiring history (for employers)
    getUserHiringHistory: (userId: string, params: {
        page?: number
        limit?: number
        status?: string
    } = {}): Promise<ApiResponse<{
        gigs: any[]
        totalCount: number
        currentPage: number
        totalPages: number
        stats: {
            gigsPosted: number
            completed: number
            averageRating: number
            totalSpent: number
        }
    }>> =>
        api.get(`/users/${userId}/hiring-history`, { params }),

    // Update user's online status
    updateOnlineStatus: (isOnline: boolean): Promise<ApiResponse> =>
        api.put('/users/online-status', { isOnline }),

    // Get user's activity feed
    getUserActivity: (userId: string, params: {
        page?: number
        limit?: number
        type?: string
    } = {}): Promise<ApiResponse<{
        activities: Array<{
            _id: string
            type: string
            description: string
            createdAt: string
            metadata?: any
        }>
        totalCount: number
        currentPage: number
        totalPages: number
    }>> =>
        api.get(`/users/${userId}/activity`, { params }),

    // Update user preferences
    updateUserPreferences: (preferences: {
        language?: string
        timezone?: string
        currency?: string
        notifications?: any
        privacy?: any
    }): Promise<ApiResponse<{ preferences: any }>> =>
        api.put('/users/preferences', preferences),

    // Get user preferences
    getUserPreferences: (): Promise<ApiResponse<{ preferences: any }>> =>
        api.get('/users/preferences'),

    // Deactivate user account
    deactivateAccount: (): Promise<ApiResponse> =>
        api.post('/users/deactivate'),

    // Delete user account permanently
    deleteAccount: (): Promise<ApiResponse> =>
        api.post('/users/delete'),

    // Export user data
    exportUserData: (): Promise<ApiResponse<{
        message: string
        exportId: string
    }>> =>
        api.post('/users/export-data'),

    // Get user data for direct download (JSON)
    getUserDataExport: (): Promise<ApiResponse<{
        userData: {
            profile: any
            preferences: any
            gigs: any[]
            applications: any[]
            messages: any[]
            reviews: any[]
            stats: any
            createdAt: string
        }
        exportedAt: string
        version: string
    }>> =>
        api.get('/users/export-data/download'),

    // Get user data as PDF download
    getUserDataExportPDF: (): Promise<Blob> =>
        fetch('/api/v1/users/export-data/pdf', {
            method: 'GET',
            headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
            }
        }).then(response => response.blob()),

    // Verify user profile (for admin or verification system)
    verifyUser: (userId: string, verificationData: {
        documentType: string
        documentNumber: string
        notes?: string
    }): Promise<ApiResponse<{ user: User }>> =>
        api.post(`/users/${userId}/verify`, verificationData),

    // Get verification status
    getVerificationStatus: (userId?: string): Promise<ApiResponse<{
        verified: boolean
        verificationLevel: 'none' | 'email' | 'phone' | 'document' | 'full'
        documents: Array<{
            type: string
            status: 'pending' | 'approved' | 'rejected'
            submittedAt: string
        }>
    }>> =>
        api.get(`/users/${userId || 'me'}/verification`),

    // Submit verification documents
    submitVerificationDocuments: (formData: FormData): Promise<ApiResponse> =>
        api.post('/users/verification/documents', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }),

    // Get user statistics
    getUserStats: (userId?: string): Promise<ApiResponse<{
        profileViews: number
        gigsPosted: number
        applicationsReceived: number
        applicationsSubmitted: number
        completedJobs: number
        averageRating: number
        totalEarnings: number
        totalSpent: number
        joinDate: string
    }>> =>
        api.get(`/users/${userId || 'me'}/stats`),
}